import { useCallback, useReducer, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { FrontendSurfaceConfig, QueryState, PaginationMeta, SavedView } from './types';

// ── State machine ─────────────────────────────────────────────────────────────

type Action =
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_SORT'; payload: string }
  | { type: 'SET_FILTER'; payload: { key: string; value: string | string[] } }
  | { type: 'CLEAR_FILTER'; payload: string }
  | { type: 'CLEAR_ALL_FILTERS' }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_VIEW_MODE'; payload: string }
  | { type: 'LOAD_VIEW'; payload: Partial<QueryState> };

function queryReducer(state: QueryState, action: Action): QueryState {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, search: action.payload, page: 1 };
    case 'SET_SORT':
      return { ...state, sort: action.payload, page: 1 };
    case 'SET_FILTER':
      return { ...state, filters: { ...state.filters, [action.payload.key]: action.payload.value }, page: 1 };
    case 'CLEAR_FILTER': {
      const { [action.payload]: _, ...rest } = state.filters;
      return { ...state, filters: rest, page: 1 };
    }
    case 'CLEAR_ALL_FILTERS':
      return { ...state, filters: {}, page: 1 };
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };
    case 'LOAD_VIEW':
      return { ...state, ...action.payload, page: 1 };
    default:
      return state;
  }
}

function buildInitialState(surface: FrontendSurfaceConfig, overrides?: Partial<QueryState>): QueryState {
  return {
    search: '',
    sort: surface.sortOptions[0]?.value ?? '',
    filters: {},
    page: 1,
    pageSize: surface.defaultPageSize ?? 20,
    viewMode: surface.defaultViewMode,
    ...overrides,
  };
}

// ── URL sync ──────────────────────────────────────────────────────────────────

function stateToParams(state: QueryState): Record<string, string> {
  const params: Record<string, string> = {};
  if (state.search) params.search = state.search;
  if (state.sort) params.sort = state.sort;
  if (state.page > 1) params.page = String(state.page);
  if (state.viewMode) params.viewMode = state.viewMode;
  for (const [k, v] of Object.entries(state.filters)) {
    params[`filter.${k}`] = Array.isArray(v) ? v.join(',') : v;
  }
  return params;
}

function paramsToState(
  searchParams: URLSearchParams,
  surface: FrontendSurfaceConfig,
): Partial<QueryState> {
  const overrides: Partial<QueryState> = {};
  const search = searchParams.get('search');
  if (search) overrides.search = search;
  const sort = searchParams.get('sort');
  if (sort) overrides.sort = sort;
  const page = searchParams.get('page');
  if (page) overrides.page = parseInt(page, 10) || 1;
  const viewMode = searchParams.get('viewMode');
  if (viewMode && surface.viewModes.some((v) => v.mode === viewMode)) {
    overrides.viewMode = viewMode;
  }
  const filters: Record<string, string> = {};
  for (const [k, v] of searchParams.entries()) {
    if (k.startsWith('filter.')) filters[k.slice(7)] = v;
  }
  if (Object.keys(filters).length > 0) overrides.filters = filters;
  return overrides;
}

// ── Main hook ─────────────────────────────────────────────────────────────────

export interface UseQueryableOptions {
  surface: FrontendSurfaceConfig;
  fetchUrl: string;
  /** URL base for saved views CRUD. Defaults to '/views' (tenant-scoped).
   *  Portal pages must pass '/portal/views' so the portal JWT is attached. */
  savedViewsUrl?: string;
  syncToUrl?: boolean;
  enabled?: boolean;
}

export interface UseQueryableResult<T> {
  data: T[];
  meta: PaginationMeta | null;
  isLoading: boolean;
  isError: boolean;
  queryState: QueryState;
  savedViews: SavedView[];
  setSearch: (v: string) => void;
  setSort: (v: string) => void;
  setFilter: (key: string, value: string | string[]) => void;
  clearFilter: (key: string) => void;
  clearAllFilters: () => void;
  setPage: (n: number) => void;
  setViewMode: (mode: string) => void;
  loadSavedView: (view: SavedView) => void;
}

export function useQueryable<T>({
  surface,
  fetchUrl,
  savedViewsUrl = '/views',
  syncToUrl = false,
  enabled = true,
}: UseQueryableOptions): UseQueryableResult<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();

  const initialOverrides = syncToUrl ? paramsToState(searchParams, surface) : undefined;
  const [state, dispatch] = useReducer(queryReducer, buildInitialState(surface, initialOverrides));

  // Sync state → URL params
  useEffect(() => {
    if (!syncToUrl) return;
    setSearchParams(stateToParams(state), { replace: true });
  }, [state, syncToUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build query params for fetch
  const params = new URLSearchParams();
  if (state.search) params.set('search', state.search);
  if (state.sort) params.set('sort', state.sort);
  params.set('page', String(state.page));
  params.set('pageSize', String(state.pageSize));
  if (state.viewMode) params.set('viewMode', state.viewMode);
  for (const [k, v] of Object.entries(state.filters)) {
    params.set(`filter.${k}`, Array.isArray(v) ? v.join(',') : v);
  }

  const queryKey = ['queryable', surface.surfaceKey, state];

  const { data: response, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await apiClient.get(`${fetchUrl}?${params.toString()}`);
      return data as { data: T[]; meta: PaginationMeta; errors: null };
    },
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const { data: savedViewsResponse } = useQuery({
    queryKey: ['saved-views', surface.surfaceKey],
    queryFn: async () => {
      const { data } = await apiClient.get(`${savedViewsUrl}?surfaceKey=${surface.surfaceKey}`);
      return data.data as SavedView[];
    },
    enabled,
    staleTime: 60_000,
  });

  const setSearch = useCallback((v: string) => dispatch({ type: 'SET_SEARCH', payload: v }), []);
  const setSort = useCallback((v: string) => dispatch({ type: 'SET_SORT', payload: v }), []);
  const setFilter = useCallback((key: string, value: string | string[]) =>
    dispatch({ type: 'SET_FILTER', payload: { key, value } }), []);
  const clearFilter = useCallback((key: string) => dispatch({ type: 'CLEAR_FILTER', payload: key }), []);
  const clearAllFilters = useCallback(() => dispatch({ type: 'CLEAR_ALL_FILTERS' }), []);
  const setPage = useCallback((n: number) => dispatch({ type: 'SET_PAGE', payload: n }), []);
  const setViewMode = useCallback((mode: string) => dispatch({ type: 'SET_VIEW_MODE', payload: mode }), []);

  const loadSavedView = useCallback((view: SavedView) => {
    const payload: Partial<QueryState> = {};
    if (view.viewConfig.sort) payload.sort = view.viewConfig.sort;
    if (view.viewConfig.filters) payload.filters = view.viewConfig.filters;
    if (view.viewConfig.viewMode) payload.viewMode = view.viewConfig.viewMode;
    dispatch({ type: 'LOAD_VIEW', payload });
    qc.invalidateQueries({ queryKey: ['queryable', surface.surfaceKey] });
  }, [surface.surfaceKey, qc]);

  return {
    data: response?.data ?? [],
    meta: response?.meta ?? null,
    isLoading,
    isError,
    queryState: state,
    savedViews: savedViewsResponse ?? [],
    setSearch,
    setSort,
    setFilter,
    clearFilter,
    clearAllFilters,
    setPage,
    setViewMode,
    loadSavedView,
  };
}
